import React, {
  useEffect, useRef, useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';

import AcceptAgreementField from '../shared/AcceptAgreementField';
import AppDownloadButtons from './AppDownloadButtons';
import CommonFields from '../shared/CommonFields';
import GoogleAuthSection from '../shared/GoogleAuthSection';
import IdntyEmailStep from './IdntyEmailStep';
import IdntyExistingUserLinks from './IdntyExistingUserLinks';
import IdntyExistingUserStep from './IdntyExistingUserStep';
import IdntyGoogleJoinModal from './IdntyGoogleJoinModal';
import IdntyGoogleOption from './IdntyGoogleOption';
import { USER_TYPE } from './IdntyDownloadView';
import RegistrationFormWrapper from '../shared/RegistrationFormWrapper';
import Button from '../../../../shared/_TBComponents/Button';
import {
  EmailOnlySchema,
  IdntyExistingUserSchema,
  RegistrationSchemaGoogle,
  RegistrationSchemaIdnty,
} from '../../../../shared/utils/registrationSchemaHelper';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { preLogin } from '../../../login/ducks/loginActions';
import {
  checkIdntyGoogleAccount,
  IDNTY_GOOGLE_STATUS,
  registerExistingUser,
  registerUser,
  resolveJoinResult,
  setGoogleUser,
  setRegisteringWithGoogle,
} from '../../ducks/registrationActions';
import useReset from '../../../forgot-password/components/hooks/useReset';

const STEPS = {
  EMAIL: 'EMAIL',
  NEW_USER: 'NEW_USER',
  EXISTING_USER: 'EXISTING_USER',
};

const STEP_SCHEMAS = {
  [STEPS.EXISTING_USER]: IdntyExistingUserSchema,
  [STEPS.EMAIL]: EmailOnlySchema,
};

// EMAIL_IN_USE isn't yet surfaced as a stable error code by the backend -
// fall back to matching the known message text until it is.
const isEmailInUseError = (err) => {
  const message = err?.response?.headers?.message || '';
  return message.toLowerCase().includes('already in use');
};

const AlreadyHaveAccountText = styled('p')`
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin: 15px 0 8px;
  text-align: center;
`;

const ForgotPasswordSentText = styled('p')`
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 15px;
  text-align: center;
`;

const FormGroup = styled('div')`
  margin-bottom: 20px;
  z-index: 1;

  label {
    width: 100%;
  }

  @media screen and (max-width: 425px) {
    margin-bottom: 10px;
  }
`;

const IdntyRegistrationForm = () => {
  const googleUser = useSelector((state) => state.registration.data.googleUser);
  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );
  const isPreLoginLoading = useSelector((state) => state.auth.ui.isPreLoginLoading);
  const isSubmitting = useSelector((state) => state.registration.ui.isSubmitting);
  const registerUserError = useSelector((state) => state.registration.data.registerUserError);

  const [googleResponse, setGoogleResponse] = useState(null);
  // Non-null while a Google sign-in is waiting on explicit confirmation (the
  // modal below) before registerExistingUser actually runs.
  const [pendingGoogleJoin, setPendingGoogleJoin] = useState(null);
  const [step, setStep] = useState(STEPS.EMAIL);
  const [cameFromDuplicateEmail, setCameFromDuplicateEmail] = useState(false);
  const [cameFromGoogleEntry, setCameFromGoogleEntry] = useState(false);
  const [isForgotPasswordSent, setIsForgotPasswordSent] = useState(false);
  const [isForgotPasswordSubmitting, setIsForgotPasswordSubmitting] = useState(false);

  const formikRef = useRef(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { handleForgot } = useReset();

  const programId = new URLSearchParams(search).get('programId');
  const easyJoinId = process.env.IDNTY_EASY_JOIN_ID;
  const isMobile = /iPad|iPhone|iPod|Android/.test(navigator.userAgent);
  const getNewUserSchema = () => (isRegisteringWithGoogle
    ? RegistrationSchemaGoogle
    : RegistrationSchemaIdnty);
  const validationSchema = STEP_SCHEMAS[step] || getNewUserSchema();
  const buttonName = {
    [STEPS.EMAIL]: 'next',
    [STEPS.EXISTING_USER]: 'join idnty',
  }[step] || 'submit';
  // Nothing destructive happens until the reset link is actually used, so
  // there's nothing to protect by hiding the rest of the form. "Go back"
  // just un-hides it again, same step, same values.
  const isForgotPasswordSentView = step === STEPS.EXISTING_USER && isForgotPasswordSent;

  // Every branch here is resolved from pre-login's response, never guessed.
  const handleEmailNext = (email) => {
    dispatch(preLogin(email, true)).then((data) => {
      if (data === undefined) {
        return;
      }
      if (data === null) {
        setStep(STEPS.NEW_USER);
      } else if (data.isIdnty) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.ALREADY_JOINED}`);
      } else if (data.hasPassword) {
        setStep(STEPS.EXISTING_USER);
      } else {
        setStep(STEPS.NEW_USER);
      }
    });
  };

  // Resolves the Google button (used from every step) to one of four
  // outcomes via checkIdntyGoogleAccount. PENDING_JOIN commits nothing - the
  // real join waits on the modal below.
  const handleGoogleEntry = (idToken) => {
    dispatch(checkIdntyGoogleAccount(idToken)).then((result) => {
      if (result.status === IDNTY_GOOGLE_STATUS.ALREADY_MEMBER) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.ALREADY_JOINED}`);
      } else if (result.status === IDNTY_GOOGLE_STATUS.PENDING_JOIN) {
        setPendingGoogleJoin({ idToken, email: result.decoded?.email });
      } else if (result.status === IDNTY_GOOGLE_STATUS.NEW_USER) {
        batch(() => {
          dispatch(setRegisteringWithGoogle(true));
          dispatch(setGoogleUser(result.decoded));
        });
        setGoogleResponse({ tokenId: idToken });
        setCameFromGoogleEntry(true);
        setStep(STEPS.NEW_USER);
      }
      // ERROR: no-op, checkIdntyGoogleAccount already showed a toast.
    });
  };

  const handleGoogleEntryError = () => {
    showErrorToast('There was an error connecting with Google, please try again.');
  };

  // The actual (real, mutating) join, deferred until the user has explicitly
  // confirmed the account shown in the modal.
  const handleConfirmGoogleJoin = () => {
    dispatch(registerExistingUser({
      authenticationMethod: 'GOOGLE',
      easyJoinId,
      googleToken: pendingGoogleJoin.idToken,
      programId,
    })).then(resolveJoinResult).then((result) => {
      setPendingGoogleJoin(null);
      if (result.status === IDNTY_GOOGLE_STATUS.ALREADY_MEMBER) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.ALREADY_JOINED}`);
      } else if (result.status === IDNTY_GOOGLE_STATUS.JOINED) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.EXISTING_USER}`);
      }
      // ERROR: no-op, registerExistingUser already showed a toast.
    });
  };

  const handleCancelGoogleJoin = () => {
    setPendingGoogleJoin(null);
  };

  const handleRegistration = (values) => {
    dispatch(registerUser({
      acceptAgreement: values.acceptAgreement,
      accountCode: process.env.IDNTY_ACCT_CODE,
      authenticationMethod: isRegisteringWithGoogle ? 'GOOGLE' : 'PASSWORD',
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      countryCode: values.phone ? values.countryCode : undefined,
      // Coerced to match registerExistingUser's contract (see registrationActions.js).
      easyJoinId: Number(easyJoinId),
      emailAddress: values.emailAddress,
      firstName: values.firstName,
      gender: values.gender,
      lastName: values.lastName,
      mobileNumber: values.phone,
      ...(programId && { programId: Number(programId) }),
      ...(isRegisteringWithGoogle && { googleToken: googleResponse?.tokenId }),
      ...(!isRegisteringWithGoogle && { password: values.password }),
      registrationType: 'EXISTING_ORGANIZATION',
    }, true));
  };

  const handleSignIn = (values) => {
    // Explicit here (not the passive isComplete redirect) since the
    // confirmation page needs to know existingUser vs. new registration,
    // only knowable at this call site.
    dispatch(registerExistingUser({
      authenticationMethod: 'PASSWORD',
      easyJoinId,
      email: values.emailAddress,
      password: values.password,
      programId,
    })).then((result) => {
      if (result?.alreadyJoined) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.ALREADY_JOINED}`);
      } else if (result?.success) {
        history.push(`/signup/idnty/success?userType=${USER_TYPE.EXISTING_USER}`);
      }
    });
  };

  const handleForgotPassword = (email) => {
    setIsForgotPasswordSubmitting(true);
    handleForgot({ email }, true, programId).then((result) => {
      setIsForgotPasswordSubmitting(false);
      if (result?.success) {
        setIsForgotPasswordSent(true);
      } else {
        showErrorToast(result?.message);
      }
    });
  };

  const handleEditEmailFromExistingUser = () => {
    formikRef.current?.setFieldValue('emailAddress', '');
    formikRef.current?.setFieldValue('password', '');
    setCameFromDuplicateEmail(false);
    setStep(STEPS.EMAIL);
  };

  const handleGoBackFromNewUser = () => {
    formikRef.current?.setFieldValue('emailAddress', '');
    formikRef.current?.setFieldValue('password', '');
    formikRef.current?.setFieldValue('confirmedPassword', '');
    setCameFromDuplicateEmail(false);
    setCameFromGoogleEntry(false);
    setGoogleResponse(null);
    batch(() => {
      dispatch(setRegisteringWithGoogle(false));
      dispatch(setGoogleUser({}));
    });
    setStep(STEPS.EMAIL);
  };

  const handleSubmit = (values) => {
    if (step === STEPS.EMAIL) {
      handleEmailNext(values.emailAddress);
    } else if (step === STEPS.EXISTING_USER) {
      handleSignIn(values);
    } else {
      handleRegistration(values);
    }
  };

  // Re-validates on step change, since Formik won't do it automatically when
  // validationSchema changes. Also clears touched - Formik's submitForm
  // marks every field in the shared values object, not just this step's.
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm();
      formikRef.current.setTouched({});
    }
  }, [step]);

  // Disconnecting Google after landing on NEW_USER via the front-page option
  // should return to the front page, mirroring "Not you?". Handled via this
  // effect (not a prop on the shared GoogleAuthSection) to avoid touching a
  // component other registration forms also use.
  useEffect(() => {
    if (cameFromGoogleEntry && !isRegisteringWithGoogle && step === STEPS.NEW_USER) {
      setCameFromGoogleEntry(false);
      setStep(STEPS.EMAIL);
    }
  }, [cameFromGoogleEntry, isRegisteringWithGoogle, step]);

  // A brand-new email that turns out to belong to an existing account
  // (EMAIL_IN_USE) re-runs the real pre-login check to route into the
  // correct existing-user step, rather than surfacing a raw error.
  useEffect(() => {
    if (step === STEPS.NEW_USER && registerUserError && isEmailInUseError(registerUserError)) {
      setCameFromDuplicateEmail(true);
      // The password just typed for a (rejected) new registration shouldn't
      // carry over as if it were the existing account's password.
      formikRef.current?.setFieldValue('password', '');
      handleEmailNext(formikRef.current?.values?.emailAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerUserError, step]);

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={{
        acceptAgreement: false,
        confirmedPassword: '',
        countryCode: 'US',
        emailAddress: googleUser.email || '',
        firstName: googleUser.givenName || '',
        gender: undefined,
        lastName: googleUser.familyName || '',
        password: '',
        phone: undefined,
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(formikBag) => {
        const {
          errors,
          isValid,
          touched,
          handleSubmit: formikHandleSubmit,
          setFieldValue,
          values,
        } = formikBag;

        // Formik v1 has no working innerRef - this is the only place the
        // live bag (values, validateForm) is available, so keep a ref to it
        // in sync here for the effects above to read.
        formikRef.current = formikBag;

        // Formik v1's isValid is unreliable across a step's schema swap, so
        // errors/values are checked directly per step instead, like
        // ExistingAccountRegistrationForm.js does.
        const isExistingUserStepInvalid = step === STEPS.EXISTING_USER
          && (!!errors.password || !values.password?.trim());
        const isNewUserStepInvalid = step === STEPS.NEW_USER
          && (
            !!errors.acceptAgreement
            || !!errors.confirmedPassword
            || !!errors.countryCode
            || !!errors.emailAddress
            || !!errors.firstName
            || !!errors.lastName
            || !!errors.phone
            || !values.acceptAgreement
            || !values.emailAddress?.trim()
            || !values.firstName?.trim()
            || !values.lastName?.trim()
            || (!isRegisteringWithGoogle
              && (!values.password?.trim() || !values.confirmedPassword?.trim()))
          );
        const isEmailStepLoading = step === STEPS.EMAIL && isPreLoginLoading;

        return (
          <>
            <IdntyGoogleJoinModal
              email={pendingGoogleJoin?.email}
              isOpen={!!pendingGoogleJoin}
              isSubmitting={isSubmitting}
              onCancel={handleCancelGoogleJoin}
              onConfirm={handleConfirmGoogleJoin}
            />
            <RegistrationFormWrapper
              subtitle={isForgotPasswordSentView ? '' : 'Sign up and download the app for access to FREE world-class training programs from top coaches in the TeamBuildr network.'}
              title={isForgotPasswordSentView ? 'Forgot password' : 'Continue Your Training Journey'}
              isIdnty
            >
              <Form
                id='registration'
                name='registration'
              >
                {isForgotPasswordSentView ? (
                  <>
                    <ForgotPasswordSentText>
                      If your email is associated with a TeamBuildr account, a
                      link to reset your password and join IDNTY will be sent to
                      your inbox.
                    </ForgotPasswordSentText>
                    <IdntyExistingUserLinks
                      editEmailLabel='Go back'
                      onEditEmail={() => setIsForgotPasswordSent(false)}
                      showForgotPassword={false}
                    />
                  </>
                ) : (
                  <>
                    {step === STEPS.EMAIL && (
                    <>
                      <IdntyGoogleOption
                        onGoogleError={handleGoogleEntryError}
                        onGoogleSuccess={handleGoogleEntry}
                      />
                      <IdntyEmailStep
                        error={errors.emailAddress}
                        touched={touched.emailAddress}
                      />
                    </>
                    )}
                    {step === STEPS.NEW_USER && (
                    <>
                      {isRegisteringWithGoogle ? (
                        <GoogleAuthSection
                          onGoogleConnect={setGoogleResponse}
                        />
                      ) : (
                        <IdntyGoogleOption
                          onGoogleError={handleGoogleEntryError}
                          onGoogleSuccess={handleGoogleEntry}
                        />
                      )}
                      <CommonFields
                        errors={errors}
                        setFieldValue={setFieldValue}
                        showPasswordFields={!isRegisteringWithGoogle}
                        touched={touched}
                        values={values}
                      />
                      <AcceptAgreementField
                        error={errors.acceptAgreement}
                        setFieldValue={setFieldValue}
                        touched={touched.acceptAgreement}
                        values={{
                          acceptAgreement: values.acceptAgreement,
                        }}
                      />
                    </>
                    )}
                    {step === STEPS.EXISTING_USER && (
                    <IdntyExistingUserStep
                      cameFromDuplicateEmail={cameFromDuplicateEmail}
                      errors={errors}
                      onGoogleError={handleGoogleEntryError}
                      onGoogleSuccess={handleGoogleEntry}
                      touched={touched}
                    />
                    )}
                    <FormGroup>
                      <Button
                        bgColor='#00F5F5'
                        disabled={
                      !isValid
                      || isSubmitting
                      || isEmailStepLoading
                      || isExistingUserStepInvalid
                      || isNewUserStepInvalid
                    }
                        fontSize='14px'
                        fullWidth
                        id='submit'
                        isLoading={isSubmitting || isEmailStepLoading}
                        large
                        lightSpinner
                        name={buttonName}
                        noBorder
                        onClick={formikHandleSubmit}
                        rounded
                        txtColor='#000000'
                        type='submit'
                        upperCase
                      />
                    </FormGroup>
                    {step === STEPS.EXISTING_USER && (
                    <IdntyExistingUserLinks
                      isForgotPasswordSubmitting={isForgotPasswordSubmitting}
                      onEditEmail={handleEditEmailFromExistingUser}
                      onForgotPassword={() => handleForgotPassword(values.emailAddress)}
                    />
                    )}
                    {step === STEPS.NEW_USER && (
                    <IdntyExistingUserLinks
                      editEmailLabel='Go back'
                      onEditEmail={handleGoBackFromNewUser}
                      showForgotPassword={false}
                    />
                    )}
                    <AlreadyHaveAccountText>
                      {isMobile
                        ? 'Already have an IDNTY account? Open the TeamBuildr Training app to sign in.'
                        : 'Already have an IDNTY account? Download the TeamBuildr Training app on your mobile device to sign in.'}
                    </AlreadyHaveAccountText>
                    <AppDownloadButtons />
                  </>
                )}
              </Form>
            </RegistrationFormWrapper>
          </>
        );
      }}
    </Formik>
  );
};

export default IdntyRegistrationForm;
